/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { type MutableValue, MutableValueImpl } from '@amazon/vinyl-observable'
import {
    createPersistentItem,
    globalRef,
    type LogTarget,
    type PersistentItem,
} from '@amazon/vinyl-util'
import type { ObjectSchema } from '@amazon/vinyl-validation'
import { boolean, isOneOf, object } from '@amazon/vinyl-validation'
import { type StreamingQuality } from '@amzn/vinyl-amzn'
import { getPlaybackEnv } from '../env/playbackEnvRef'
import { PLAYBACK_STORAGE_KEYS } from '../env/playbackStorageKeys'
import { storePersistence } from '../env/storePersistence'
import {
    downgradeStreamingQuality,
    loadPlayableAudioQualities,
    playableAudioQualities$,
} from './audioQualityEntitlement'

export interface StreamingSettings {
    readonly streamingQuality: StreamingQuality
    readonly abrEnabled: boolean
    readonly normalizeVolume: boolean
}

export const streamingSettingsValidator: ObjectSchema<StreamingSettings> = object({
    streamingQuality: isOneOf('HD', 'STANDARD', 'DATA_SAVER'),
    abrEnabled: boolean(),
    normalizeVolume: boolean(),
})

export function defaultStreamingSettings(): StreamingSettings {
    return { streamingQuality: 'STANDARD', abrEnabled: true, normalizeVolume: true }
}

function createStreamingSettingsItem(): PersistentItem<StreamingSettings> {
    return createPersistentItem<StreamingSettings>({
        key: PLAYBACK_STORAGE_KEYS.streamingSettings,
        defaultValue: defaultStreamingSettings(),
        validator: streamingSettingsValidator.assert,
        persistence: storePersistence,
    })
}

export interface StreamingSettingsController extends MutableValue<StreamingSettings | null> {
    /** Settles once the current value is stored; rejects when storing it fails. */
    readonly persisted: Promise<void>
}

export class StreamingSettingsControllerImpl
    extends MutableValueImpl<StreamingSettings | null>
    implements LogTarget
{
    readonly logPrefix = 'StreamingControllerImpl'
    private readonly errorHandler = (error: unknown) =>
        getPlaybackEnv().errors.handleError(this, error)
    persisted: Promise<void> = Promise.resolve()

    constructor() {
        super(null)
        const streamingQualitySettingsLocal = createStreamingSettingsItem()
        streamingQualitySettingsLocal
            .get()
            .then((value) => {
                this.value = value
                this.sanitizeAgainstEntitlement()
            })
            .catch(this.errorHandler)
        this.onData((value) => {
            if (!value) return
            this.persisted = streamingQualitySettingsLocal.set(value)
            this.persisted.catch(this.errorHandler)
        })
        loadPlayableAudioQualities().catch(this.errorHandler)
        // Persisted settings and the account's playable qualities resolve
        // independently; re-run the downgrade whenever the entitlement lands
        // so it fires regardless of which arrives first.
        playableAudioQualities$.onData(() => this.sanitizeAgainstEntitlement())
    }

    // Downgrade a persisted tier the account has lost entitlement to
    // (HD → Standard → Data Saver), mirroring green hornet's
    // sanitizePersistedStreamingQuality. No-op until both the persisted
    // settings and the account's playable qualities are known.
    private sanitizeAgainstEntitlement(): void {
        const qualities = playableAudioQualities$.value
        if (!qualities) return
        const settings = this.value
        if (!settings) return
        const streamingQuality =
            downgradeStreamingQuality(settings.streamingQuality, qualities) ??
            settings.streamingQuality
        if (streamingQuality !== settings.streamingQuality) {
            this.value = { ...settings, streamingQuality }
        }
    }
}

const streamingSettingsControllerRef = globalRef<StreamingSettingsController>(() => {
    return new StreamingSettingsControllerImpl()
})

export function getStreamingSettingsController() {
    return streamingSettingsControllerRef.value
}
